import { cn } from '@repo/tailwind-config/tailwind.functions'
import { UnitBasedInput, UnitBaseInputValue } from '@repo/ui/unit-based-input'
import React, { useEffect, useState } from 'react'

import { CurrencyEnum, currencyEnumMapping } from '../../../lib/types/enums/common/currency.enum'

export type CurrencyInputProps = {
  readonly?: boolean
  errorMessage?: string
  className?: string
  disabled?: boolean
  value?: Partial<Omit<UnitBaseInputValue<CurrencyEnum>, 'icon'>>
  onChange: (value: Omit<UnitBaseInputValue<CurrencyEnum>, 'icon'>) => Promise<void> | void
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({ className, value, onChange, ...props }) => {
  const [inputValue, setInputValue] = useState(value?.value)
  const [currency, setCurrency] = useState(value?.selectedOptionValue)
  const [loading, setLoading] = useState(false)

  const [pendingValue, setPendingValue] = useState<{
    value: string
    currency: CurrencyEnum
  } | null>(null)

  const [isEditing, setIsEditing] = useState(false)

  const handleChange = (newCurrency: CurrencyEnum, newValue?: string) => {
    setCurrency(newCurrency)
    setIsEditing(true)

    setInputValue(newValue)
    setPendingValue({ value: newValue || '', currency: newCurrency })
  }

  useEffect(() => {
    if (!pendingValue) return

    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        await onChange({
          value: pendingValue.value,
          selectedOptionValue: pendingValue.currency
        })
      } finally {
        setLoading(false)
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [pendingValue])

  useEffect(() => {
    if (!isEditing) {
      setInputValue(value?.value)
      setCurrency(value?.selectedOptionValue)
    }
  }, [value?.value, value?.selectedOptionValue, isEditing])

  return (
    <UnitBasedInput<CurrencyEnum>
      {...props}
      className={cn('w-32', className)}
      disabled={loading || props.disabled}
      options={Object.values(CurrencyEnum).map((currency) => ({
        value: currency,
        label: currencyEnumMapping[currency].label,
        icon: currencyEnumMapping[currency].icon
      }))}
      value={{
        value: inputValue,
        selectedOptionValue: currency,
        icon: currency ? currencyEnumMapping[currency].icon : undefined
      }}
      onChange={(v) => {
        if (!v || !v.selectedOptionValue) return
        handleChange(v.selectedOptionValue, v.value)
      }}
    />
  )
}

export default CurrencyInput
